// UserManagement.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Divider, TablePagination, Tooltip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import { fetchUsers, fetchRoles, createUser, updateUser, deleteUser, setTemporaryPassword } from "../services/userService";
import LoadingSpinner from "../components/LoadingSpinner";
import { formatPhoneNumber } from "../shared/formatter";
import PhoneInput from "../components/PhoneInput";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const roleColor = (role) => {
  switch (role) {
    case "Admin": return "error";
    case "Manager": return "primary";
    case "Desk Officer": return "secondary";
    default: return "default";
  }
};

const statusColor = (isActive) => (isActive ? "success" : "default");

const extractApiError = (error) => {
  if (!error) return "Unable to complete the request.";
  if (typeof error === "string") return error;
  if (error.message) return error.message;
  return "Unable to complete the request.";
};

const fadeIn = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.35, ease: "easeOut" },
  }),
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({
    full_name: "", email: "", phone_number: "", password: "", groups: "", is_active: true
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, rolesData] = await Promise.all([fetchUsers(), fetchRoles()]);
      setUsers(usersData.map(u => ({ ...u, roleName: rolesData.find(r => r.id === u.groups)?.name || 'N/A' })));
      setRoles(rolesData);
      setError(null);
    } catch (err) {
      setError(err.message);
      Swal.fire({ icon: "error", title: "Failed to load data", text: err.message, confirmButtonColor: brand });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.full_name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone_number?.toLowerCase().includes(q) ||
        u.roleName?.toLowerCase().includes(q)
    );
  }, [query, users]);

  const paginated = useMemo(() => {
    if (rowsPerPage === -1) return filtered;
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const handleChangePage = (_e, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    const val = parseInt(e.target.value, 10);
    setRowsPerPage(val);
    setPage(0);
  };

  const handleOpenAdd = () => {
    setDialogMode("add");
    setForm({
      full_name: "", email: "", phone_number: "", password: "", groups: roles[0]?.id || "", is_active: true
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (user) => {
    setDialogMode("edit");
    setEditingUser(user);
    setForm({
      full_name: user.full_name,
      email: user.email,
      phone_number: user.phone_number,
      password: "", // Don't pre-fill password
      groups: user.groups,
      is_active: user.is_active,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleSave = async () => {
    if (!form.full_name || !form.phone_number || (dialogMode === 'add' && !form.password)) {
      Swal.fire({
        icon: "warning",
        title: "Missing fields",
        text: "Full Name, Phone Number, and Password (for new users) are required.",
        confirmButtonColor: brand,
      });
      return;
    }

    if (dialogMode === "add") {
      const newUser = { id: Date.now(), ...form, phone_number: formatPhoneNumber(form.phone_number) };
      try {
        await createUser(newUser);
        Swal.fire({ icon: "success", title: "User created", confirmButtonColor: brand });
        handleCloseDialog();
        loadData();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Creation Failed",
          text: extractApiError(err),
          confirmButtonColor: brand,
        });
      }
    } else {
      try {
        const passwordToSet = form.password?.trim();
        const updateData = { ...form, phone_number: formatPhoneNumber(form.phone_number) };
        delete updateData.password;

        await updateUser(editingUser.id, updateData);

        if (passwordToSet) {
          await setTemporaryPassword(editingUser.id, passwordToSet);
        }

        Swal.fire({
          icon: "success",
          title: "User updated",
          text: passwordToSet
            ? "Temporary password set. User will be forced to reset password on first login."
            : undefined,
          confirmButtonColor: brand,
        });
        handleCloseDialog();
        loadData();
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: extractApiError(err),
          confirmButtonColor: brand,
        });
      }
    }
  };

  const handleDelete = (user) => {
    Swal.fire({
      title: "Delete user?",
      text: `Are you sure you want to delete ${user.full_name}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: brand,
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Delete",
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await deleteUser(user.id);
          Swal.fire({ icon: "success", title: "Deleted!", text: `${user.full_name}'s account has been removed.`, confirmButtonColor: brand });
          loadData(); // Refresh data
        } catch (err) {
          Swal.fire({ icon: "error", title: "Deletion Failed", text: err.message, confirmButtonColor: brand });
        }
      }
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
          User Management
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          Create and manage staff accounts (Admin, Manager, Desk Officer)
        </Typography>
      </Box>

      {/* Toolbar */}
      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by name, email, or role..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
              >
                Add User
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <LoadingSpinner />}
      {error && !loading && (
        <Typography color="error" align="center" sx={{ my: 4 }}>
          Failed to load users: {error}
        </Typography>
      )}
      {/* Table */}
      <motion.div variants={fadeIn} initial="hidden" animate="visible">
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    !loading && <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        No users found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((u, idx) => (
                      <TableRow key={u.id} hover>
                        <TableCell>{rowsPerPage === -1 ? idx + 1 : page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell>{u.full_name}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">{u.phone_number}</Typography>
                            <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={u.roleName} size="small" color={roleColor(u.roleName)} sx={{ fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={u.is_active ? 'Active' : 'Inactive'} size="small" color={statusColor(u.is_active)} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton onClick={() => handleOpenEdit(u)} sx={{ mr: .5 }}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton onClick={() => handleDelete(u)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />

            {/* Bottom-right pagination */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2 }}>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage === -1 ? filtered.length : rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100, { label: "All", value: -1 }]}
                labelRowsPerPage="Rows per page:"
              />
            </Box>
          </CardContent>
        </Card>
      </motion.div>

      {/* Add / Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {dialogMode === "add" ? "Add User" : "Edit User"}
          <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: .5 }}>
            <Grid item xs={12} md={6}>
              <TextField label="Full Name" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <PhoneInput
                value={form.phone_number}
                onChange={(val) => setForm((f) => ({ ...f, phone_number: val }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Email (Optional)" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={dialogMode === "add" ? "Password" : "Temporary Password"}
                type="password"
                required={dialogMode === 'add'}
                placeholder={dialogMode === 'edit' ? 'Set a temporary password (user will reset on first login)' : ''}
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  value={form.groups}
                  onChange={(e) => setForm((f) => ({ ...f, groups: e.target.value }))}
                >
                  {roles.map(r => (
                    <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.value }))}
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
